// `extension-alerts-candidates-filter-tooltip.tpl.html` — the menu behind the "Filters" button.
//
// Two collapsible groups, "Group" (which candidates appear at all) and "Last CV date". Each
// shows the current selection as a row that toggles its own list open.
//
// ⚠️ THE TWO GROUPS COLLAPSE INDEPENDENTLY AND BOTH START CLOSED, which is what
// `collapsibleFiltersState` holds. Opening the tooltip onto an expanded list would be a
// different control: the point of the collapsed rows is that the menu shows the CURRENT
// filters first and the options only on request.
import { type ReactElement } from 'react'
import {
  CV_DATE_OPTIONS,
  SHOWN_OPTION_DEFAULT,
  CV_DATE_OPTION_DEFAULT,
  selectedOptionIcon,
  type DateFilterOption,
  type ShowFilterOption,
} from './candidateFilters.ts'

/** `ctrl.separator.value` — the row the template renders as a divider rather than an option. */
export const CV_DATE_SEPARATOR_VALUE = 9999

export interface CandidateFiltersTooltipProps {
  shownOption: ShowFilterOption
  cvDateOption: DateFilterOption
  /** The options offered — `shownCandidatesOptions`, plus Exported/Messaged where enabled. */
  shownOptions: readonly ShowFilterOption[]
  groupOpen?: boolean
  cvDateOpen?: boolean
  onToggleGroup?: () => void
  onToggleCvDate?: () => void
  onSelectShown?: (option: ShowFilterOption) => void
  onSelectCvDate?: (option: DateFilterOption) => void
}

export function CandidateFiltersTooltip({
  shownOption,
  cvDateOption,
  shownOptions,
  groupOpen = false,
  cvDateOpen = false,
  onToggleGroup,
  onToggleCvDate,
  onSelectShown,
  onSelectCvDate,
}: CandidateFiltersTooltipProps): ReactElement {
  const icon = selectedOptionIcon(shownOption)

  return (
    <div className="extension-candidates-filters-tooltip-container">
      <div className="extension-candidates-filters-tooltip-group-container">
        <span className="filters-side-padding">Group</span>
        {/* `default-option` marks the selection as untouched — it is how the menu greys the
            default without comparing objects. Compared by NAME, as everything else here is. */}
        <div
          className={[
            'cursor-pointer',
            'hover-lighter',
            'extension-candidates-filters-tooltip-item-selected',
            'filters-side-padding',
            shownOption.name === SHOWN_OPTION_DEFAULT ? 'default-option' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          title={shownOption.name}
          role="button"
          tabIndex={0}
          onClick={onToggleGroup}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onToggleGroup?.()
            }
          }}
        >
          {icon === undefined ? null : <i className={icon} />}
          <strong className="slim-filters-show-text extra-cut truncate">&nbsp;{shownOption.name}</strong>
          <i className={groupOpen ? 'fas fa-caret-left' : 'fas fa-caret-down'} />
        </div>
        {/* `uib-collapse` renders its content and animates the height; an unmounted list would
            lose the transition the stylesheet expects, so this hides rather than omits. */}
        <div hidden={!groupOpen}>
          {shownOptions.map((option) => (
            <div key={option.name}>
              <span
                className="cursor-pointer extension-candidates-filters-tooltip-item"
                role="button"
                tabIndex={0}
                onClick={() => onSelectShown?.(option)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelectShown?.(option)
                  }
                }}
              >
                <i className={option.cssClass} />
                &nbsp;&nbsp;
                {option.name}
              </span>
            </div>
          ))}
        </div>
      </div>
      <hr className="extension-candidates-filters-group-divider" />
      <div className="extension-candidates-filters-tooltip-group-container">
        <span className="filters-side-padding">Last CV date</span>
        <div
          className={[
            'cursor-pointer',
            'hover-lighter',
            'extension-candidates-filters-tooltip-item-selected',
            'filters-side-padding',
            cvDateOption.name === CV_DATE_OPTION_DEFAULT ? 'default-option' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          title={cvDateOption.name}
          role="button"
          tabIndex={0}
          onClick={onToggleCvDate}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onToggleCvDate?.()
            }
          }}
        >
          {/* ⚠️ THE SELECTED ROW SHOWS `shortName`, THE LIST SHOWS `name`. "< 7d" against
              "Last 7 days" — the row is inside a 200px frame and the list is not. */}
          <strong className="slim-filters-relevancy-text truncate">{cvDateOption.shortName}</strong>{' '}
          <i className={cvDateOpen ? 'fas fa-caret-left' : 'fas fa-caret-down'} />
        </div>
        <div hidden={!cvDateOpen}>
          {CV_DATE_OPTIONS.map((option, index) => (
            // ⚠️ THE SEPARATOR IS A ROW, NOT A GAP. Upstream keeps it in the array, gives its
            // wrapper `divider` and renders no span inside — so the rule is laid out by the
            // list rather than by a margin. `track by $index`, because two options can share a
            // name only if someone edits the table.
            <div key={index} className={option.value === CV_DATE_SEPARATOR_VALUE ? 'divider' : undefined}>
              {option.value === CV_DATE_SEPARATOR_VALUE ? null : (
                <span
                  className="cursor-pointer extension-candidates-filters-tooltip-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectCvDate?.(option)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onSelectCvDate?.(option)
                    }
                  }}
                >
                  {option.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
